import type { MeCourt, MeSport } from "@/lib/me";

export interface CalendarScope {
  id: string;
  courts: MeCourt[];
  sports: MeSport[];
  sessionDurationMinutes: number;
  workingHoursStart: string;
  workingHoursEnd: string;
}

interface BaseLayout {
  scope: CalendarScope;
  sportFilterAvailable: boolean;
}

export type SingleLayout = { kind: "single" } & BaseLayout;

interface CourtTabLayout {
  id: string;
  courtId: string;
  courtName: string;
  scope: CalendarScope;
}

export type CourtTabsLayout = {
  kind: "courtTabs";
  tabs: CourtTabLayout[];
};

export type CombinedLayout = { kind: "combined" } & BaseLayout;

interface SportTab {
  id: string;
  sportId: string;
  sportName: string;
  inner: SingleLayout | CourtTabsLayout;
}

export type SportTabsLayout = {
  kind: "sportTabs";
  tabs: SportTab[];
};

export type Layout =
  | SingleLayout
  | CourtTabsLayout
  | CombinedLayout
  | SportTabsLayout;

interface DeriveInput {
  sports: MeSport[];
  courts: MeCourt[];
  clubWorkingHoursStart: string;
  clubWorkingHoursEnd: string;
}

function effectiveStart(c: MeCourt, fallback: string): string {
  return c.workingHoursStart || fallback;
}
function effectiveEnd(c: MeCourt, fallback: string): string {
  return c.workingHoursEnd || fallback;
}

function allCourtsShareStart(courts: MeCourt[], fallback: string): boolean {
  if (courts.length <= 1) return true;
  const start = effectiveStart(courts[0], fallback);
  return courts.every((c) => effectiveStart(c, fallback) === start);
}

function maxEnd(courts: MeCourt[], fallback: string): string {
  // HH:mm strings compare lexicographically when zero-padded.
  return courts.reduce(
    (max, c) =>
      effectiveEnd(c, fallback) > max ? effectiveEnd(c, fallback) : max,
    effectiveEnd(courts[0], fallback),
  );
}

function buildSingleScope(
  id: string,
  courts: MeCourt[],
  sports: MeSport[],
  fallbackStart: string,
  fallbackEnd: string,
): CalendarScope {
  // All courts here are assumed to share the same effective hours (caller's
  // responsibility). We just pick the first court's effective hours.
  const first = courts[0];
  const sport = sports[0];
  return {
    id,
    courts,
    sports,
    sessionDurationMinutes: sport?.sessionDurationMinutes ?? 60,
    workingHoursStart: first
      ? effectiveStart(first, fallbackStart)
      : fallbackStart,
    workingHoursEnd:
      courts.length > 0 ? maxEnd(courts, fallbackEnd) : fallbackEnd,
  };
}

export interface LayoutSelection {
  scope: CalendarScope;
  /** When the layout supports filtering by sport (combined view). */
  sportFilter: string | null;
  /** ID of the active sport tab (sportTabs layout only). */
  activeSportId: string | null;
  /** ID of the active court tab (courtTabs or nested under sportTabs). */
  activeCourtId: string | null;
}

/**
 * Picks the active scope for a layout from URL search params. Mirrors the
 * tab-resolution logic that used to live inside the per-variant components so
 * the chooser UI can be hoisted out of the calendar render path.
 */
export function resolveLayoutSelection(
  layout: Layout,
  params: URLSearchParams,
): LayoutSelection {
  if (layout.kind === "single") {
    return {
      scope: layout.scope,
      sportFilter: null,
      activeSportId: null,
      activeCourtId: null,
    };
  }

  if (layout.kind === "courtTabs") {
    const courtParam = params.get("court");
    const active =
      layout.tabs.find((tab) => tab.courtId === courtParam) ?? layout.tabs[0];
    return {
      scope: active.scope,
      sportFilter: null,
      activeSportId: null,
      activeCourtId: active.courtId,
    };
  }

  if (layout.kind === "combined") {
    const filterParam = params.get("sportFilter");
    const isValid =
      filterParam && layout.scope.sports.some((s) => s.id === filterParam);
    return {
      scope: layout.scope,
      sportFilter: isValid ? filterParam : null,
      activeSportId: null,
      activeCourtId: null,
    };
  }

  // sportTabs — may have an inner courtTabs layout, so check the court param too.
  const sportParam = params.get("sport");
  const activeSport =
    layout.tabs.find((tab) => tab.sportId === sportParam) ?? layout.tabs[0];
  if (activeSport.inner.kind === "single") {
    return {
      scope: activeSport.inner.scope,
      sportFilter: null,
      activeSportId: activeSport.sportId,
      activeCourtId: null,
    };
  }
  const courtParam = params.get("court");
  const activeCourt =
    activeSport.inner.tabs.find((tab) => tab.courtId === courtParam) ??
    activeSport.inner.tabs[0];
  return {
    scope: activeCourt.scope,
    sportFilter: null,
    activeSportId: activeSport.sportId,
    activeCourtId: activeCourt.courtId,
  };
}

export function deriveLayout(input: DeriveInput): Layout | null {
  const { sports, courts, clubWorkingHoursStart, clubWorkingHoursEnd } = input;
  const activeCourts = courts.filter((c) => c.isActive);
  if (activeCourts.length === 0 || sports.length === 0) return null;

  const courtsBySport = new Map<string, MeCourt[]>();
  for (const c of activeCourts) {
    const arr = courtsBySport.get(c.sportId) ?? [];
    arr.push(c);
    courtsBySport.set(c.sportId, arr);
  }
  // Only consider sports that actually have at least one active court.
  const sportsWithCourts = sports.filter(
    (s) => (courtsBySport.get(s.id) ?? []).length > 0,
  );
  if (sportsWithCourts.length === 0) return null;

  // ── Single sport ──────────────────────────────────────────────────────────
  if (sportsWithCourts.length === 1) {
    const sport = sportsWithCourts[0];
    const sportCourts = courtsBySport.get(sport.id) ?? [];
    const uniform = allCourtsShareStart(sportCourts, clubWorkingHoursStart);

    if (uniform) {
      return {
        kind: "single",
        scope: buildSingleScope(
          `sport:${sport.id}`,
          sportCourts,
          [sport],
          clubWorkingHoursStart,
          clubWorkingHoursEnd,
        ),
        sportFilterAvailable: false,
      };
    }

    return {
      kind: "courtTabs",
      tabs: sportCourts.map((c) => ({
        id: `court:${c.id}`,
        courtId: c.id,
        courtName: c.name,
        scope: buildSingleScope(
          `court:${c.id}`,
          [c],
          [sport],
          clubWorkingHoursStart,
          clubWorkingHoursEnd,
        ),
      })),
    };
  }

  // ── Multiple sports ───────────────────────────────────────────────────────
  const allShareStart = allCourtsShareStart(
    activeCourts,
    clubWorkingHoursStart,
  );
  const allShareDuration = sportsWithCourts.every(
    (s) =>
      s.sessionDurationMinutes === sportsWithCourts[0].sessionDurationMinutes,
  );

  if (allShareStart && allShareDuration) {
    const first = activeCourts[0];
    return {
      kind: "combined",
      scope: {
        id: "combined",
        courts: activeCourts,
        sports: sportsWithCourts,
        sessionDurationMinutes: sportsWithCourts[0].sessionDurationMinutes,
        workingHoursStart: effectiveStart(first, clubWorkingHoursStart),
        workingHoursEnd: maxEnd(activeCourts, clubWorkingHoursEnd),
      },
      sportFilterAvailable: true,
    };
  }

  return {
    kind: "sportTabs",
    tabs: sportsWithCourts.map<SportTab>((sport) => {
      const sportCourts = courtsBySport.get(sport.id) ?? [];
      const uniform = allCourtsShareStart(sportCourts, clubWorkingHoursStart);

      if (uniform) {
        return {
          id: `sport:${sport.id}`,
          sportId: sport.id,
          sportName: sport.name,
          inner: {
            kind: "single",
            scope: buildSingleScope(
              `sport:${sport.id}`,
              sportCourts,
              [sport],
              clubWorkingHoursStart,
              clubWorkingHoursEnd,
            ),
            sportFilterAvailable: false,
          },
        };
      }

      return {
        id: `sport:${sport.id}`,
        sportId: sport.id,
        sportName: sport.name,
        inner: {
          kind: "courtTabs",
          tabs: sportCourts.map((c) => ({
            id: `court:${c.id}`,
            courtId: c.id,
            courtName: c.name,
            scope: buildSingleScope(
              `court:${c.id}`,
              [c],
              [sport],
              clubWorkingHoursStart,
              clubWorkingHoursEnd,
            ),
          })),
        },
      };
    }),
  };
}
